import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit-html';
import '@zywave/zui-colors';
import '@zywave/zui-icons';
/**
 * ## Installation
 * ### With npm:
 * ```shell
 * > npm i--save @zywave/zui-well
 * ```
 * ### With yarn:
 * ```shell
 * > yarn add @zywave/zui-well
 * ```
 *
 * ## Basic Usage
 * ```html
 * <zui-well type="info">
 *    <div>
 *      Employees must sign their own enrollment forms.
 *    </div>
 *    <zui-well-actions>
 *      <button>Remind employees</button>
 *    </zui-well-actions>
 *  </zui-well>
 * ```
 *
 * zui-well is a grouping of an icon, text, and a background color to emphasize text
 */

class ZuiWell extends ZuiBaseElement {
  static get properties() {
    return {
      /**
       * The type of well you want to display. Defaults to 'info'.
       * The types of zui-well include: 'info', 'warning', 'error'
       *
       */
      type: {
        type: String,
        reflect: true
      },

      /**
       * Whether or not you want to display an icon in your well
       */
      noIcon: {
        type: Boolean,
        attribute: 'no-icon'
      }
    };
  }

  connectedCallback() {
    super.connectedCallback(); // TODO(pat) investigate if this is the right place to set "reflect" attributes. Seems weird.

    if (!this.type) {
      this.type = 'info';
    }
  }

  _renderStyle() {
    return html`
      <style>
        :host{position:relative;display:-webkit-box;display:-ms-flexbox;display:flex;-ms-flex-wrap:wrap;flex-wrap:wrap;width:100%;max-width:500px;padding:0;max-height:0;opacity:0;color:var(--zui-gray-700);-webkit-animation-name:well--slide-down;animation-name:well--slide-down;-webkit-animation-duration:200ms;animation-duration:200ms;-webkit-animation-delay:0.8s;animation-delay:0.8s;-webkit-animation-timing-function:ease-out;animation-timing-function:ease-out;-webkit-animation-fill-mode:forwards;animation-fill-mode:forwards}:host([no-icon]) .icon{display:none}:host([type='info']){background-color:var(--zui-light-blue-50);--zui-well-action-color: var(--zui-light-blue-500);--zui-well-action-hover-color: var(--zui-light-blue-600)}:host([type='info']) zui-icon{color:var(--zui-light-blue-500)}:host([type='warning']){background-color:var(--zui-tangerine-50);--zui-well-action-color: var(--zui-tangerine-500);--zui-well-action-hover-color: var(--zui-tangerine-600)}:host([type='warning']) zui-icon{color:var(--zui-tangerine-500)}:host([type='error']){background-color:var(--zui-red-50);--zui-well-action-color: var(--zui-red-500);--zui-well-action-hover-color: var(--zui-red-600)}:host([type='error']) zui-icon{color:var(--zui-red-500)}.icon{display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;-webkit-box-align:center;-ms-flex-align:center;align-items:center;vertical-align:top;padding:0;margin:0;height:22px;width:22px;margin-right:15px;opacity:0;-webkit-animation-name:well-icon--grow;animation-name:well-icon--grow;-webkit-animation-duration:200ms;animation-duration:200ms;-webkit-animation-delay:1.2s;animation-delay:1.2s;-webkit-animation-timing-function:ease-out;animation-timing-function:ease-out;-webkit-animation-fill-mode:forwards;animation-fill-mode:forwards}.icon zui-icon{--iron-icon-height: 22px;--iron-icon-width: 22px}.text{display:inline-block;vertical-align:top;line-height:1.6;-webkit-box-flex:1;-ms-flex:1;flex:1;opacity:0;-webkit-animation-name:well-text--slide-down;animation-name:well-text--slide-down;-webkit-animation-duration:200ms;animation-duration:200ms;-webkit-animation-delay:1s;animation-delay:1s;-webkit-animation-timing-function:ease-out;animation-timing-function:ease-out;-webkit-animation-fill-mode:forwards;animation-fill-mode:forwards}@media all and (-ms-high-contrast: none){.text{opacity:1}}@-webkit-keyframes well-icon--grow{0%{opacity:0;-webkit-transform:scale(0);transform:scale(0)}100%{opacity:1;-webkit-transform:scale(1);transform:scale(1)}}@keyframes well-icon--grow{0%{opacity:0;-webkit-transform:scale(0);transform:scale(0)}100%{opacity:1;-webkit-transform:scale(1);transform:scale(1)}}@-webkit-keyframes well-text--slide-down{0%{opacity:0;max-height:0}100%{max-height:100%;opacity:1}}@keyframes well-text--slide-down{0%{opacity:0;max-height:0}100%{max-height:100%;opacity:1}}@-webkit-keyframes well--slide-down{0%{opacity:0;max-height:0;margin:0}100%{max-height:100%;padding:15px;margin:20px 0;opacity:1}}@keyframes well--slide-down{0%{opacity:0;max-height:0;margin:0}100%{max-height:100%;padding:15px;margin:20px 0;opacity:1}}
      </style>
    `;
  }

  render() {
    return html`
      ${this._renderStyle()}
      <div class="icon"> <zui-icon icon="${this._computeIcon(this.type)}" class="icon-size"></zui-icon> </div>
      <div class="text"> <slot></slot> </div>
    `;
  }
  /**
   *
   * @param {string} type Computes the _icon property. This determines which zui-icon this well will use.
   */


  _computeIcon(type) {
    const prefix = 'zui';
    let icon = undefined;

    switch (type) {
      case 'info':
        icon = 'info';
        break;

      case 'warning':
        icon = 'alert-circle';
        break;

      case 'error':
        icon = 'alert-circle';
        break;

      default:
        icon = 'info';
        break;
    }

    return `${prefix}-${icon}`;
  }

}

customElements.define('zui-well', ZuiWell);